import {
  CardStyleInterpolators,
  StackScreenProps,
} from "@react-navigation/stack";
import {
  Button,
  ModalCloseButton,
  RootDrawerToggle,
  createStackHeaderNavigator,
  useWithAppState,
} from "@towersystems/roam-common-native";
import useOnline from "@towersystems/roam-common-native/lib/utilities/use-online";
import { useMemo } from "react";
import {
  StockCountsRootParamList,
  ValidSetupRootParamList,
} from "../../../../types";
import { useSetupAllStartupEntities } from "../../../../utilities/setup";
import { ProductFormActionsContext } from "../../../products/components/ProductForm/context";
import { StockCountProvider, StockCountSyncProvider } from "../../components";
import { StockCountCompletedRootScreen } from "../StockCountCompletedRootScreen";
import { StockCountsScreen } from "../StockCountsScreen";
import { StockCountsScreenOffline } from "../StockCountsScreenOffline/StockCountsScreenOffline";
import { useTranslation } from "../../../../utilities/i18n";
import { StockCountFilterRootScreen } from "../StockCountFilterRootScreen";
import { ListFilterProvider } from "../../../../utilities/list-filter/use-list-filter-context";
import { useListFilter } from "../../../../utilities/list-filter";
import { StockCountFilterValues } from "../../types";
import { defaultStockCountsFilterValues } from "../../common";
import { PerformStockCountRootScreen } from "../PerformStockCountRootScreen";
import { StartStockCountRootScreen } from "../StartStockCountRootScreen";
import React from "react";

export interface StockCountsRootNavigationProps
  extends StackScreenProps<
    ValidSetupRootParamList & StockCountsRootParamList,
    "StockCountsRootScreen"
  > {}

const StockCountsRoot = ({ navigation }: StockCountsRootNavigationProps) => {
  const { t } = useTranslation("stock_counts");
  const Stack = createStackHeaderNavigator<StockCountsRootParamList>();
  const isOnline = useOnline();

  const screenProps = {
    headerTitleAlign: "center",
    headerLeft: () => <RootDrawerToggle />,
    title: "",
  } as const;

  const defaultScreen = useMemo(
    () =>
      isOnline ? (
        <Stack.Screen
          name="StockCountsScreen"
          component={StockCountsScreen}
          options={screenProps}
        />
      ) : undefined,
    [isOnline],
  );

  return (
    <Stack.Navigator>
      {defaultScreen}
      <Stack.Screen
        name="StockCountsScreenOffline"
        component={StockCountsScreenOffline}
        options={screenProps}
      />
      <Stack.Group
        screenOptions={{
          animationEnabled: true,
          cardStyleInterpolator: CardStyleInterpolators.forHorizontalIOS,
          headerLeft: () => <ModalCloseButton type="Back" />,
        }}
      >
        <Stack.Screen
          name="PerformStockCountRootScreen"
          component={PerformStockCountRootScreen}
          options={{
            headerShown: false,
          }}
        />
        <Stack.Screen
          name="StartStockCountRootScreen"
          component={StartStockCountRootScreen}
          options={{
            headerShown: false,
          }}
        />
        <Stack.Screen
          name="StockCountCompletedRootScreen"
          component={StockCountCompletedRootScreen}
          options={{
            headerShown: false,
          }}
        />
      </Stack.Group>
      <Stack.Group
        screenOptions={{
          headerShown: false,
          presentation: "modal",
          animationEnabled: true,
        }}
      >
        <Stack.Screen
          name="StockCountFilterRootScreen"
          component={StockCountFilterRootScreen}
        />
      </Stack.Group>
    </Stack.Navigator>
  );
};

export const StockCountsRootScreen = (
  props: StockCountsRootNavigationProps,
) => {
  const { fetchAndSyncEntity } = useSetupAllStartupEntities();

  const {
    appState: { locationId },
  } = useWithAppState();

  const filterContext = useListFilter<StockCountFilterValues>({
    defaultValues: {
      ...defaultStockCountsFilterValues,
      locationId: [locationId],
    },
  });

  return (
    <ListFilterProvider {...filterContext}>
      <ProductFormActionsContext.Provider
        value={{
          onAfterAddProduct: async (_, navigation) => {
            await fetchAndSyncEntity("products");
            navigation.goBack();
          },
        }}
      >
        <StockCountSyncProvider>
          <StockCountProvider>
            <StockCountsRoot {...props} />
          </StockCountProvider>
        </StockCountSyncProvider>
      </ProductFormActionsContext.Provider>
    </ListFilterProvider>
  );
};
